import CUSTOM_DOMAINS from './customDomains';
// Sift4 - simplest version
// online algorithm to compute the distance between two strings in O(n)
// maxOffset is the number of characters to search for matching letters
function sift4(s1: string, s2: string, maxOffset = 5) {
    if (!s1 || !s1.length) {
        if (!s2) {
            return 0;
        }
        return s2.length;
    }

    if (!s2 || !s2.length) {
        return s1.length;
    }

    const l1 = s1.length;
    const l2 = s2.length;

    let c1 = 0; //cursor for string 1
    let c2 = 0; //cursor for string 2
    let lcss = 0; //largest common subsequence
    let local_cs = 0; //local common substring
    while (c1 < l1 && c2 < l2) {
        if (s1.charAt(c1) == s2.charAt(c2)) {
            local_cs++;
        } else {
            lcss += local_cs;
            local_cs = 0;
            if (c1 != c2) {
                c1 = c2 = Math.max(c1, c2); //using max to bypass the need for computer transpositions ('ab' vs 'ba')
            }
            for (let i = 0; i < maxOffset && (c1 + i < l1 || c2 + i < l2); i++) {
                if (c1 + i < l1 && s1.charAt(c1 + i) == s2.charAt(c2)) {
                    c1 += i;
                    local_cs++;
                    break;
                }
                if (c2 + i < l2 && s1.charAt(c1) == s2.charAt(c2 + i)) {
                    c2 += i;
                    local_cs++;
                    break;
                }
            }
        }
        c1++;
        c2++;
    }
    lcss += local_cs;
    return Math.round(Math.max(l1, l2) - lcss);
}

function normalizeString(input: string): string {
    return input
        .trim()
        .toLowerCase()
        .replace(/[Ａ-Ｚａ-ｚ０-９．]/g, (char) => {
            const code = char.charCodeAt(0);
            if (code === 65294) {
                return '.';
            } else {
                return String.fromCharCode(code - 65248);
            }
        })
        .replace(/\s/g, '')
        .replace(/\.{2,}/g, '.');
}

function getClosestDomainWithWildcard(normalizedDomain: string, currDomainItem: string) {
    const normalizedDomainParts = normalizedDomain.split('.');
    const currDomainItemParts = currDomainItem.split('.');

    const closestDomainParts = [];

    for (let i = 0; i < currDomainItemParts.length; i++) {
        if (
            i < normalizedDomainParts.length &&
            (normalizedDomainParts[i] === currDomainItemParts[i] || currDomainItemParts[i] === '*')
        ) {
            closestDomainParts.push(
                currDomainItemParts[i] === '*' ? normalizedDomainParts[i] : currDomainItemParts[i],
            );
        } else {
            closestDomainParts.push(currDomainItemParts[i]);
        }
    }

    return closestDomainParts.join('.');
}

export function findClosestDomain(
    userInputdomain: string | undefined,
    domains: string[],
    distanceFunction = sift4,
    threshold = 3,
) {
    let dist;
    let minDist = Infinity;
    let closestDomain = null;
    const specialDomainRegexp = /jp-[a-zA-Z0-9]+\.ne\.jp/i;

    if (!userInputdomain || !domains || specialDomainRegexp.test(userInputdomain)) return false;

    const normalizedDomain = normalizeString(userInputdomain);
    for (let i = 0; i < domains.length; i++) {
        const currDomainItem = domains[i];
        if (currDomainItem.startsWith('*')) {
            if (
                normalizedDomain.endsWith(currDomainItem.slice(1)) &&
                normalizedDomain === userInputdomain
            ) {
                return false;
            } else {
                dist = distanceFunction(
                    normalizedDomain.split('.').slice(1).join(),
                    currDomainItem.split('.').slice(1).join(),
                );
            }
            if (dist < minDist) {
                minDist = dist;
                closestDomain = getClosestDomainWithWildcard(normalizedDomain, currDomainItem);
            }
        } else {
            dist = distanceFunction(normalizedDomain, currDomainItem);
            if (userInputdomain === currDomainItem) {
                return false;
            }
            if (dist < minDist) {
                minDist = dist;
                closestDomain = currDomainItem;
            }
        }
    }

    if (minDist <= threshold && closestDomain !== null) {
        if (closestDomain.indexOf('*.') !== -1) {
            return closestDomain.replace('*', normalizedDomain.split('.')[0]);
        }
        return closestDomain;
    } else {
        return false;
    }
}

export function findClosestEmailUsername(userInputEmailUsername: string | undefined) {
    if (!userInputEmailUsername) return false;
    const normalizedUsername = normalizeString(userInputEmailUsername);
    const isNormal = normalizedUsername === userInputEmailUsername;
    return isNormal ? false : normalizedUsername;
}

export function findClosestFullEmail(userInputEmail: string | undefined) {
    if (!userInputEmail || userInputEmail.split('@').length !== 2) return false;
    const [username, domain] = userInputEmail.split('@');
    const closestEmailUsername = findClosestEmailUsername(username);
    const closestDomain = findClosestDomain(domain, CUSTOM_DOMAINS);
    return closestDomain || closestEmailUsername
        ? { domain: closestDomain || domain, username: closestEmailUsername || username }
        : undefined;
}
